import z from 'zod';

import { ParagraphCodec, PlateParagraph, UnityParagraph } from './Paragraph';
import {
    CURRENT_VERSION,
    ElementId,
    EmptyChildren,
    emptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityFrontendComponent = z.object({
    type: z.literal(ELEMENT_TYPES.FRONTEND_COMPONENT),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    mimetype: z.literal(MIME_TYPES.FRONTEND_COMPONENT),
    src: z.string(),
    originalSrc: z.string().nullable(),
    contentAttributes: z.object({
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        credit: z.string().nullable(),
        alt: z.string().nullable(),
        label: z.string(),
        properties: z.array(z.record(z.string(), z.string())),
    }),
});
export type UnityFrontendComponent = z.infer<typeof UnityFrontendComponent>;

export const PlateFrontendComponent = z.object({
    type: z.literal(ELEMENT_TYPES.FRONTEND_COMPONENT),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        mimetype: z.literal(MIME_TYPES.FRONTEND_COMPONENT),
        originalSrc: z.string(),
        embed: z.object({
            src: z.string(),
            label: z.string(),
            alt: z.string().nullable(),
            properties: z.array(z.record(z.string(), z.string())),
            caption: z.array(PlateParagraph),
            credit: z.string(),
        }),
    }),
    children: EmptyChildren,
});
export type FrontendComponentElement = z.infer<typeof PlateFrontendComponent>;

export const FrontendComponentCodec = z.codec(UnityFrontendComponent, PlateFrontendComponent, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
            mimetype: value.mimetype,
            originalSrc: value.originalSrc ?? '',
            embed: {
                src: value.src,
                label: value.contentAttributes.label,
                alt: value.contentAttributes.alt,
                properties: value.contentAttributes.properties,
                caption: (value.contentAttributes.caption?.richTextFormat || []).map((paragraph) =>
                    ParagraphCodec.decode(paragraph),
                ),
                credit: value.contentAttributes.credit ?? '',
            },
        },
        children: emptyChildren,
    }),

    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...(value.data.templateElement ? { templateElement: value.data.templateElement } : {}),
        mimetype: value.data.mimetype,
        src: value.data.embed.src,
        originalSrc: value.data.originalSrc,
        contentAttributes: {
            caption: {
                richTextFormat: value.data.embed.caption.map((paragraph) =>
                    ParagraphCodec.encode(paragraph),
                ),
            },
            credit: value.data.embed.credit,
            alt: value.data.embed.alt,
            label: value.data.embed.label,
            properties: value.data.embed.properties,
        },
    }),
});
